import * as B from '@blueprintjs/core';
import {DateRange, DateRangePicker3} from '@blueprintjs/datetime2';
import moment from 'moment';
import React from 'react';

import cs from 'app/components/Dashboard/styles/chart.styl';

// we want to be able to group our pie charts by multiple fields,
// so this is an object with the metadata field and human readable label
// for displaying. ie, if we are grouping by portfolioId, we'd pass our
// metadata field "portfolioId" and label "Portfolio"
export type PieGroup = {
  label: string;
  id: string;
  // this reflects whether a record can belong to more than one pie slice
  // eg, a property can only be in 1 portfolio, but can have many tags.
  // this is used to determine if we can show valid percentages.
  singleMembership: boolean;
  groupingField: string;
  labelField: string;
};

type BaseProps = {
  pieGroups: PieGroup[];
  selectedPieGroup: PieGroup;
  setSelectedPieGroup: React.Dispatch<React.SetStateAction<PieGroup>>;
};

type WithDateRange = {
  showDateRangeInput: true;
  dateRange: DateRange;
  setDateRange: (dateRange: DateRange) => void;
};

type WithoutDateRange = {
  showDateRangeInput: false;
  // these must not exist when showDateRangeInput is false
  dateRange?: never;
  setDateRange?: never;
};

type PieGroupControlsProps = BaseProps & (WithDateRange | WithoutDateRange);

export const PieGroupControls: React.FunctionComponent<PieGroupControlsProps> = ({
  pieGroups,
  selectedPieGroup,
  setSelectedPieGroup,
  showDateRangeInput,
  dateRange,
  setDateRange,
}) => {
  const [selectedOption, setSelectedOption] = React.useState(DATE_RANGE_OPTIONS(dateRange)[1]);
  const [datePickerOpen, setDatePickerOpen] = React.useState(false);

  return (
    <div className={cs.pieChartControls}>
      <B.Popover
        content={
          <B.Menu>
            {pieGroups.map((grouping) => (
              <B.MenuItem
                key={grouping.id}
                icon={grouping.id === selectedPieGroup.id ? 'tick' : undefined}
                text={grouping.label}
                onClick={() => setSelectedPieGroup(grouping)}
              />
            ))}
          </B.Menu>
        }
        placement="bottom-start"
        minimal
      >
        <B.Button
          small
          minimal
          outlined
          rightIcon="caret-down"
          text={`Group by ${selectedPieGroup.label}`}
        />
      </B.Popover>

      {showDateRangeInput && (
        <B.Popover
          content={
            <B.Menu>
              {DATE_RANGE_OPTIONS(dateRange)
                .filter((dateRangeOption) => dateRangeOption.id !== 'custom')
                .map((option) => (
                  <B.MenuItem
                    shouldDismissPopover={option.id !== 'custom'}
                    key={option.id}
                    text={option.label}
                    onClick={() => {
                      setSelectedOption(option);
                      setDateRange(option.getDateRange());
                    }}
                  />
                ))}
              <B.MenuDivider />
              {DATE_RANGE_OPTIONS(dateRange)
                .filter((dateRangeOption) => dateRangeOption.id === 'custom')
                .map((option) => (
                  <B.MenuItem
                    key={option.id}
                    text={option.label}
                    onClick={() => {
                      setDatePickerOpen(true);
                    }}
                    popoverProps={{
                      captureDismiss: true,
                      isOpen: datePickerOpen,
                      onClose: () => setDatePickerOpen(false),
                      interactionKind: 'click',
                    }}
                  >
                    <DateRangePicker3 value={dateRange} onChange={setDateRange} />
                  </B.MenuItem>
                ))}
            </B.Menu>
          }
          placement="bottom-start"
          minimal
        >
          <B.Button
            minimal
            small
            outlined
            rightIcon="caret-down"
            text={`Time range: ${selectedOption.label}`}
          />
        </B.Popover>
      )}
    </div>
  );
};

type DateRangeOption = {
  id: string;
  label: string;
  getDateRange: () => DateRange;
};

const DATE_RANGE_OPTIONS: (dateRange: DateRange | undefined) => DateRangeOption[] = (dateRange) => [
  {
    id: 'last-month',
    label: 'Last month',
    getDateRange: () => [moment().subtract(1, 'month').toDate(), moment().toDate()],
  },
  {
    id: '1-year',
    label: '1 year',
    getDateRange: () => [moment().subtract(1, 'year').toDate(), moment().toDate()],
  },
  {
    id: '2-years',
    label: '2 years',
    getDateRange: () => [moment().subtract(2, 'years').toDate(), moment().toDate()],
  },
  {
    id: 'all-time',
    label: 'All time',
    getDateRange: () => [null, null],
  },
  {
    id: 'custom',
    label: dateRange?.some((date) => !!date)
      ? `${dateRange[0] ? `${moment(dateRange[0]).format('LL')} - ` : 'All through '} ${dateRange[1] ? `${moment(dateRange[1]).format('LL')}` : ' Present'}`
      : 'Custom',
    getDateRange: () => dateRange ?? [null, null],
  },
];
